import {
    Container,
    Logo,
    Modal,
    ModalContent,
    Spinner,
} from '@beat-venues/ui/react';
import { useQuery } from '@tanstack/react-query';
import { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { LayoutPreferences } from '../../components';
import { UserContext } from '../../context';
import { useAnalytics } from '../../hooks/use-analytics';
import { getCookie, isValidEmail, sanitizeQueryParams } from '../../utils';
import { Whatsapp } from '../../components/layout-preferences/whatsapp';
import { usePreferencesStore } from '../../states/preferences-state';

export const Preferences = () => {
    const { user } = useContext(UserContext);
    const [searchParams] = useSearchParams();
    const safeParams = sanitizeQueryParams(searchParams);

    const visitorHash = safeParams.hash;
    const rawVisitorEmail = safeParams.email;
    const visitorEmail =
        rawVisitorEmail && rawVisitorEmail.includes(' ')
            ? rawVisitorEmail.replace(/ /g, '+')
            : rawVisitorEmail;
    const source = safeParams.source;
    const [isAuthenticated, setIsAuthenticated] = useState(true);

    const { preferences, setPreferences } = usePreferencesStore();

    const { isFetching } = useQuery({
        queryKey: ['get-preferences', user || visitorEmail],
        queryFn: async () => {
            if (user && user.email && user.sfContactId) {
                const result = await fetch(
                    `/api/preferences/getPreferences/?email=${user?.email}&id=${user?.sfContactId}`
                );
                if (result.ok) {
                    setPreferences(await result.json());
                }
            } else if (isValidEmail(visitorEmail) && visitorHash) {
                const result = await fetch(
                    `/api/preferences/getPreferences/?email=${visitorEmail}&id=${visitorHash}`
                );
                if (result.ok) {
                    setPreferences(await result.json());
                }
            }
            return '200 OK';
        },
        enabled: !preferences?.length, // Only fetch if preferences are not already set
    });

    const { triggerPageView } = useAnalytics();

    useEffect(() => {
        triggerPageView();
    }, []);

    useEffect(() => {
        const checkCookie = () => {
            const authCookie = getCookie('token');
            setIsAuthenticated(!!authCookie || !!user || isFetching);
        };

        checkCookie();
    }, [isFetching, user]);

    if (!isAuthenticated || (user && !isFetching && source === 'WhatsApp')) {
        return (
            <Modal
                className="lg:max-auto md:max-w-screen-3xl w-full bg-gray-700 text-white md:my-0 md:max-h-screen md:rounded-none"
                isShown={true}
                onClose={() => undefined}
            >
                <ModalContent>
                    <Container className="p-6 lg:px-8" maxWidth="lg">
                        <Logo
                            className="relative h-[60px] lg:h-[66px]"
                            href="/"
                            venueId="my-beat"
                        />
                    </Container>
                    {source === 'WhatsApp' &&
                    ((visitorHash && isValidEmail(visitorEmail)) || user) ? (
                        <Whatsapp
                            userId={visitorHash ?? user?.sfContactId ?? ''}
                            email={visitorEmail ?? user?.email ?? ''}
                            userPreferences={preferences ?? []}
                        />
                    ) : (
                        <LayoutPreferences
                            isUserPending={isFetching}
                            userId={user?.sfContactId ?? visitorHash ?? ''}
                        />
                    )}
                </ModalContent>
            </Modal>
        );
    }

    return (
        <div className="relative">
            {isFetching && (
                <Spinner
                    className="absolute left-[40%] top-[15%] z-10 lg:left-1/2 lg:top-1/2"
                    venueId="beat"
                />
            )}
            <LayoutPreferences
                isAuthenticated={isAuthenticated}
                isUserPending={isFetching}
                user={user}
                userId={user?.sfContactId ?? visitorHash ?? ''}
            />
        </div>
    );
};
